import { ReactNode, useMemo, useState } from "react";
import Link from "next/link";
import { customEvent, MissParlorEventTriggerAndTargetType } from "goi_common";

import { ArrowRightIcon } from "@/components/DirectionsIcons";
import { toManwon } from "@/utils/formatMoney";
import { RegionBurial } from "@/utils/regionFacilities";

type FilterKey =
  | "all"
  | "public"
  | "private"
  | "Charnel"
  | "NaturalBurial"
  | "Cemetery";

/**
 * 운영형태와 유형을 한 줄에 섞어 단일 선택으로 둔다.
 *
 * 두 축을 각각 다중 선택으로 만들지 않은 이유: 한 지역의 장지가 대개 10곳 미만이라
 * 교차 필터를 걸면 결과가 0건이 되는 조합이 대부분이다. 장례식장 목록 필터와도
 * 조작 방식이 같아진다.
 */
const FILTERS: Array<{
  key: FilterKey;
  label: string;
  trigger: MissParlorEventTriggerAndTargetType["prlr_region"];
}> = [
  { key: "all", label: "전체", trigger: "filter_burial_all" },
  { key: "public", label: "공설", trigger: "filter_burial_public" },
  { key: "private", label: "사설", trigger: "filter_burial_private" },
  { key: "Charnel", label: "봉안", trigger: "filter_burial_charnel" },
  { key: "NaturalBurial", label: "자연장", trigger: "filter_burial_natural" },
  { key: "Cemetery", label: "묘지", trigger: "filter_burial_cemetery" },
];

/** 같은 시군구 장지를 전부 담는 페이지가 있어 최대 85곳까지 온다 */
const VISIBLE_STEP = 12;

const matches = (burial: RegionBurial, filter: FilterKey) => {
  if (filter === "all") return true;
  if (filter === "public") return burial.isPublic;
  if (filter === "private") return !burial.isPublic;
  return burial.type === filter;
};

interface RegionBurialsProps {
  sigunguName: string;
  burials: RegionBurial[];
  /** 섹션 맨 아래에 붙일 것. 인근 지역 장지 링크가 들어온다 */
  footer?: ReactNode;
}

export const RegionBurials = ({
  sigunguName,
  burials,
  footer,
}: RegionBurialsProps) => {
  const [filter, setFilter] = useState<FilterKey>("all");
  const [expanded, setExpanded] = useState(false);

  // 결과가 0건인 칩은 그리지 않는다. 누르면 빈 목록이 되는 칩을 남길 이유가 없다
  const options = useMemo(
    () =>
      FILTERS.filter(
        (option) =>
          option.key === "all" ||
          burials.some((burial) => matches(burial, option.key))
      ),
    [burials]
  );

  const filtered = useMemo(
    () => burials.filter((burial) => matches(burial, filter)),
    [burials, filter]
  );

  const shown = expanded ? filtered : filtered.slice(0, VISIBLE_STEP);
  const hiddenCount = filtered.length - shown.length;

  if (!burials.length) return null;

  return (
    <section
      className="rg-section"
      id="burial"
      aria-labelledby="rg-burial-title"
    >
      <header className="rg-section__head rg-section__head--stack">
        <h2 id="rg-burial-title" className="rg-section__title">
          {`${sigunguName} 인근 장지 `}
          <span className="rg-accent">{burials.length}곳</span>
        </h2>
        <p className="rg-section__desc">
          봉안, 자연장, 묘지 유형별 최저 분양가입니다. 공설 시설은 관내 거주
          요건과 사용 기간 제한이 있어, 표시 금액의 적용 조건을 함께 확인하셔야
          합니다.
        </p>
      </header>

      {/* 칩이 전체 하나만 남으면 필터가 의미 없다 */}
      {options.length > 1 && (
        <div className="rg-filterbar" role="group" aria-label="장지 필터">
          {options.map((option) => (
            <button
              key={option.key}
              type="button"
              className={`rg-fchip${
                filter === option.key ? " rg-fchip--on" : ""
              }`}
              aria-pressed={filter === option.key}
              onClick={() => {
                setFilter(option.key);
                setExpanded(false);
                customEvent({
                  newGtm: {
                    path: "prlr_region",
                    trigger_and_target: option.trigger,
                  },
                });
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      <div className="rg-resultbar">
        <span className="rg-resultbar__count">
          {`${filtered.length.toLocaleString("ko-KR")}개`}
        </span>
      </div>

      <ul className="rg-grid rg-grid--3">
        {shown.map((burial) => (
          <li key={burial.id} className="rg-card">
            <div className="rg-card__tags">
              <span
                className={`rg-badge${
                  burial.isPublic ? " rg-badge--dark" : ""
                }`}
              >
                {burial.isPublic ? "공설" : "사설"}
              </span>
              <span className="rg-badge rg-badge--soft">
                {burial.typeLabel}
              </span>
              {burial.isSameSigungu && (
                <span className="rg-badge rg-badge--soft">관내</span>
              )}
            </div>

            <h3 className="rg-card__title rg-card__title--sm">
              {burial.companyName}
            </h3>

            <p className="rg-card__meta">
              {burial.address}
              {!burial.isSameSigungu &&
                burial.distanceKm !== null &&
                `, 약 ${burial.distanceKm}km`}
            </p>

            <p className="rg-price__inline">
              <strong className="rg-price__value">
                {toManwon(burial.minCharge) ?? "문의"}
              </strong>
              {burial.minCharge !== null && (
                <span className="rg-price__label">부터</span>
              )}
            </p>

            {burial.chargeDetail && (
              <p className="rg-card__note">{burial.chargeDetail}</p>
            )}

            {/* 같은 facilities_detail.json 을 쓰므로 여기 뜬 장지는 전부
                /facility/{id}/ 페이지가 있다. 크로스도메인으로 내보내지 않고
                내부 링크로 흘려서 지역 페이지 -> 상세 링크 그래프를 만든다 */}
            <Link
              className="rg-detail"
              href={`/facility/${burial.id}/`}
              onClick={() =>
                customEvent({
                  newGtm: {
                    path: "prlr_region",
                    trigger_and_target: "link_burial",
                  },
                })
              }
            >
              <span>자세히 알아보기</span>
              <ArrowRightIcon size={13} />
            </Link>
          </li>
        ))}
      </ul>

      {(hiddenCount > 0 || expanded) && filtered.length > VISIBLE_STEP && (
        <button
          type="button"
          className="rg-btn rg-btn--ghost rg-more"
          onClick={() => {
            customEvent({
              newGtm: {
                path: "prlr_region",
                trigger_and_target: expanded ? "btn_fold" : "btn_more",
              },
            });
            setExpanded((prev) => !prev);
          }}
        >
          {expanded ? "접기" : `장지 ${hiddenCount}곳 더보기`}
        </button>
      )}

      {footer}
    </section>
  );
};
